import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Shield, ShieldCheck, X, Search } from "lucide-react";

export default function CoDirectorsPanel({ id, coDirectors, onChanged }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const cds = coDirectors || [];

  const search = async (v) => {
    setQ(v);
    if (v.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/members/search?q=${encodeURIComponent(v.trim())}`); setResults(data.slice(0, 6)); } catch { /* noop */ }
  };
  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  if (!open) return (
    <button data-testid="co-td-toggle" onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-3 py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors mb-4">
      <Shield className="w-3.5 h-3.5" /> Co-Directors {cds.length > 0 && `(${cds.length})`}
    </button>
  );

  return (
    <div className="border border-pb-cyan/30 bg-pb-cyan/5 p-3 mb-4" data-testid="co-td-panel">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-black uppercase tracking-[0.25em] text-pb-cyan">Co-Directors — added TDs can look; grant full clearance to let them manage</span>
        <button data-testid="co-td-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
      {cds.length === 0 && <div className="text-xs text-zinc-600 mb-2">No co-directors yet — search a member below to add one.</div>}
      <div className="space-y-1.5 mb-3">
        {cds.map((c) => (
          <div key={c.user_id} className="flex items-center gap-2 border border-pb-border/60 px-3 py-2" data-testid={`co-td-row-${c.user_id}`}>
            <span className="text-sm font-semibold text-white flex-1">{c.name}</span>
            <button data-testid={`co-td-access-${c.user_id}`} disabled={busy}
              onClick={() => run(() => api.put(`/tournaments/${id}/co-directors/${c.user_id}`, { full_access: !c.full_access }),
                c.full_access ? "Clearance revoked — view only" : "Full TD clearance granted")}
              className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${
                c.full_access ? "border-[#A3E635] text-[#A3E635] bg-[#A3E635]/10" : "border-pb-border text-zinc-500 hover:border-[#A3E635] hover:text-[#A3E635]"}`}>
              {c.full_access ? <ShieldCheck className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
              {c.full_access ? "Full Clearance" : "View Only — Grant"}
            </button>
            <button data-testid={`co-td-remove-${c.user_id}`} disabled={busy}
              onClick={() => window.confirm(`Remove ${c.name} as co-director?`) && run(() => api.delete(`/tournaments/${id}/co-directors/${c.user_id}`), "Co-director removed")}
              className="text-zinc-600 hover:text-pb-red"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="relative">
        <div className="flex items-center gap-2 bg-pb-elevated border border-pb-border px-3 py-2 focus-within:border-pb-cyan">
          <Search className="w-4 h-4 text-zinc-500" />
          <input data-testid="co-td-search" value={q} onChange={(e) => search(e.target.value)} placeholder="Search members to add as co-director…"
            className="flex-1 bg-transparent text-sm text-white placeholder-zinc-600 focus:outline-none" />
        </div>
        {results.length > 0 && (
          <div className="absolute z-20 inset-x-0 top-full bg-pb-surface border border-pb-border max-h-48 overflow-y-auto">
            {results.filter((r) => !cds.some((c) => c.user_id === r.id)).map((r) => (
              <button key={r.id} data-testid={`co-td-add-${r.id}`} disabled={busy}
                onClick={() => run(() => api.post(`/tournaments/${id}/co-directors`, { user_id: r.id }).then(() => { setQ(""); setResults([]); }), `${r.name} added as co-director (view only)`)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-pb-elevated transition-colors">
                <UserPlus className="w-4 h-4 text-pb-cyan" /> {r.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
